'use client';

import { useTranslations } from 'next-intl';

import { Carousel, LinkButton } from '@/components/molecules';
import { CarouselShell } from '@/components/molecules/Carousel/CarouselShell';

import { EnrichedContentCard, ContentCardType } from '../ContentCard';

import type { CardCarouselProps, CardDataType } from './types';

const EnrichedCarouselItem = ({
  item,
  index,
}: {
  item: CardDataType;
  index: number;
}) => {
  const shouldEnrich = Boolean(
    item.contentType === 'library' &&
    item.id &&
    typeof item.id === 'string' &&
    item.id.includes('playlist/') &&
    item.fetchPersonalPlaylistWithTracks,
  );

  return (
    <EnrichedContentCard
      key={index}
      id={item.id}
      title={item.title}
      description={item.description}
      imageUrl={item.imageUrl}
      contentType={item.contentType as ContentCardType}
      variant={item.variant}
      className="w-full"
      episodeIndex={item.episodeIndex}
      rawData={item.rawData}
      fetchPersonalPlaylistWithTracks={item.fetchPersonalPlaylistWithTracks}
      shouldEnrich={shouldEnrich}
    />
  );
};

export const CardCarousel = ({
  items,
  title,
  variant = 'default',
  showAllURL,
  hideTitle = false,
}: CardCarouselProps) => {
  const t = useTranslations();
  const params = new URLSearchParams(showAllURL?.split('?')[1] ?? showAllURL);
  const widgetId = params.get('widgetId');

  return (
    <div
      className="w-full overflow-hidden"
      data-widget-id={widgetId}
      id={`card-carousel-${widgetId}`}
    >
      <CarouselShell
        title={hideTitle ? undefined : title}
        titleClassName="mx-4"
        headerAction={
          showAllURL ? (
            <LinkButton
              variant="tertiary"
              href={showAllURL}
              size="sm"
              className="mx-4 whitespace-nowrap"
              onClick={() => {
                if (typeof window !== 'undefined' && widgetId) {
                  sessionStorage.setItem('lastVisitedWidget', widgetId);
                }
              }}
            >
              {t('FOCAL_NAV_SEE_ALL')}
            </LinkButton>
          ) : undefined
        }
      >
        <Carousel
          spaceBetween={10}
          effect={variant}
          navigation={true}
          pagination={false}
          autoplay={false}
          type={items && items.length > 0 ? items[0].contentType : undefined}
        >
          {items.map((item, index) => (
            <EnrichedCarouselItem key={index} item={item} index={index} />
          ))}
        </Carousel>
      </CarouselShell>
    </div>
  );
};
